import * as React from 'react'
import { Link } from 'react-router-dom'

import { type PMComponentRef } from '@axilon-apps/domains-ontology-ui-state'

import { Badge } from '../ui/badge'
import { plantHref } from '@atbash-labs/plantview-links'
import { cn } from '../../lib/utils'

export type ComponentChipScope = PMComponentRef['scope']

export interface ComponentChipProps {
  component: PMComponentRef
  className?: string
  /** Hide the leading scope badge. Default: shown. */
  hideScope?: boolean
}

const SCOPE_LABEL: Record<ComponentChipScope, string> = {
  equipment: 'eq',
  process_line: 'line',
  tag: 'tag'
}

/** Deep-link for a ComponentRef — always via the entity resolver. */
export function componentChipHref(refValue: PMComponentRef): string {
  return plantHref(refValue.id)
}

/**
 * Inline chip for a ComponentRef. Renders the resolved name (falling back
 * to the raw id) with a small scope badge. Links through the plant entity
 * resolver unless `linkable` is false (unresolved path / name).
 */
export function ComponentChip({
  component,
  className,
  hideScope = false
}: ComponentChipProps): React.ReactElement {
  const display = component.name?.trim() || component.id
  const title = `${component.scope} · ${component.id}`
  const badgeClass = cn('gap-1.5 px-2 py-0.5 font-mono text-11', className)
  const badge = (
    <>
      {!hideScope && (
        <span className="text-ink-3 uppercase tracking-wide">{SCOPE_LABEL[component.scope]}</span>
      )}
      <span>{display}</span>
    </>
  )

  if (component.linkable === false) {
    return (
      <Badge variant="outline" className={badgeClass} data-scope={component.scope} title={title}>
        {badge}
      </Badge>
    )
  }

  return (
    <Badge asChild variant="outline" className={badgeClass} data-scope={component.scope}>
      <Link to={componentChipHref(component)} title={title}>
        {badge}
      </Link>
    </Badge>
  )
}
